import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import rfidService, {
  AssetScan,
  Reader,
} from '../../../../services/rfidService';
import toast from '../../../../utils/toast';
import { downloadBlob } from '@/utils/helpers';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/config/constants';
import { TableView } from '@/components/common/Table';
import { TableHeader } from '@/components/common/Table/table.types';
import { useDebounce } from '@/hooks/useDebounce';

// Extended type for API response (backend may return asset_name, reader_name)
interface AssetScanWithNames extends AssetScan {
  asset_name?: string;
  reader_name?: string;
}

const AssetScanList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [scans, setScans] = useState<AssetScanWithNames[]>([]);
  const [readers, setReaders] = useState<Reader[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter states (initialize from URL if present)
  const [assetSearch, setAssetSearch] = useState<string>(
    () => queryParams.get('asset') || ''
  );
  const [epcSearch, setEpcSearch] = useState<string>(
    () => queryParams.get('epc_id') || ''
  );
  const [readerFilter, setReaderFilter] = useState<string>(
    () => queryParams.get('reader') || ''
  );
  const [timestampDateRange, setTimestampDateRange] = useState<
    [string, string]
  >(() => [
    queryParams.get('start_date') || '',
    queryParams.get('end_date') || '',
  ]);
  const [isVehicleFilter, setIsVehicleFilter] = useState<string>(
    () => queryParams.get('is_vehicle') || ''
  );

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const debouncedAssetSearch = useDebounce(assetSearch, 500);
  const debouncedEpcSearch = useDebounce(epcSearch, 500);

  useEffect(() => {
    fetchReaders();
  }, []);

  useEffect(() => {
    fetchScans();
  }, [
    page,
    rowsPerPage,
    debouncedAssetSearch,
    debouncedEpcSearch,
    readerFilter,
    timestampDateRange,
    isVehicleFilter,
    sortKey,
    sortDirection,
  ]);

  const fetchReaders = async () => {
    try {
      const response = await rfidService.getReaders({
        page_size: 100,
      });
      setReaders(response.results ?? []);
    } catch (error) {
      console.error('Error fetching readers:', error);
    }
  };

  const fetchScans = async () => {
    try {
      setLoading(true);

      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
      };

      if (debouncedAssetSearch?.toString().trim()) {
        params.asset = debouncedAssetSearch.toString().trim();
      }
      if (debouncedEpcSearch?.toString().trim()) {
        params.epc_id = debouncedEpcSearch.toString().trim();
      }
      if (readerFilter) {
        params.reader = readerFilter;
      }
      if (timestampDateRange[0]) {
        params.start_date = timestampDateRange[0];
      }
      if (timestampDateRange[1]) {
        params.end_date = timestampDateRange[1];
      }
      if (isVehicleFilter) {
        params.is_vehicle = isVehicleFilter;
      }
      if (sortKey) {
        params.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
      }

      const response = await rfidService.getAssetScans(params);
      setScans(response.results ?? []);
      setTotalCount(response.count ?? 0);
    } catch (error) {
      console.error('Error fetching asset scans:', error);
      toast.error('Failed to fetch asset scans');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (key: string) => {
    setSortKey(key);
    setSortDirection((prev) =>
      sortKey === key && prev === 'asc' ? 'desc' : 'asc'
    );
    setPage(0);
  };

  const handleExportScans = async () => {
    try {
      setExporting(true);

      const params: Record<string, string> = {};
      if (assetSearch?.toString().trim())
        params.asset = assetSearch.toString().trim();
      if (epcSearch?.toString().trim())
        params.epc_id = epcSearch.toString().trim();
      if (readerFilter) params.reader = readerFilter;
      if (timestampDateRange[0]) params.start_date = timestampDateRange[0];
      if (timestampDateRange[1]) params.end_date = timestampDateRange[1];
      if (isVehicleFilter) params.is_vehicle = isVehicleFilter;

      const blob = await rfidService.exportAssetScans(params);

      downloadBlob(blob, `asset-scans-export-${new Date().toISOString().split('T')[0]}.csv`);

      toast.success('Asset scans exported successfully');
    } catch (error) {
      console.error('Error exporting asset scans:', error);
      toast.error('Failed to export asset scans');
    } finally {
      setExporting(false);
    }
  };

  const getReaderName = (readerId: string) => {
    const reader = readers.find((r) => r.id === readerId);
    return reader ? reader.name : readerId;
  };

  const readerOptions = useMemo(
    () => [
      { label: 'All Readers', value: '' },
      ...readers.map((r) => ({ label: r.name, value: r.id })),
    ],
    [readers]
  );

  const vehicleTypeOptions = [
    { label: 'All', value: '' },
    { label: 'Vehicles Only', value: 'true' },
    { label: 'Non-Vehicles Only', value: 'false' },
  ];

  const headerDetails: TableHeader<AssetScanWithNames>[] = useMemo(
    () => [
      {
        key: 'asset',
        label: 'Asset',
        type: 'text',
        sortable: true,
        sortKey: 'asset',
        filterValue: assetSearch,
        onFilterChange: (v) => {
          setAssetSearch((v as string) ?? '');
          setPage(0);
        },
        filterPlaceholder: 'Search asset ID/name...',
        render: (row) => (
          <Link
            to={`/assets/${row.asset}`}
            onClick={(e) => e.stopPropagation()}
          >
            {(row as AssetScanWithNames).asset_name || row.asset}
          </Link>
        ),
        minWidth: 160,
      },
      {
        key: 'epc_id',
        label: 'EPC ID',
        type: 'text',
        sortable: true,
        sortKey: 'epc_id',
        filterValue: epcSearch,
        onFilterChange: (v) => {
          setEpcSearch((v as string) ?? '');
          setPage(0);
        },
        filterPlaceholder: 'Search EPC ID...',
        minWidth: 140,
      },
      {
        key: 'reader',
        label: 'Reader',
        type: 'select',
        sortable: true,
        sortKey: 'reader__name',
        filterValue: readerFilter,
        onFilterChange: (v) => {
          setReaderFilter((v as string) ?? '');
          setPage(0);
        },
        dropdownOptions: readerOptions,
        filterAllLabel: 'All Readers',
        render: (row) => (
          <Link
            to={`/rfid/readers/${row.reader}`}
            onClick={(e) => e.stopPropagation()}
          >
            {(row as AssetScanWithNames).reader_name ||
              getReaderName(row.reader)}
          </Link>
        ),
        minWidth: 160,
      },
      {
        key: 'timestamp',
        label: 'Timestamp',
        type: 'datePicker',
        sortable: true,
        sortKey: 'timestamp',
        filterValue: timestampDateRange,
        onFilterChange: (v) => {
          const range = v as [string, string];
          setTimestampDateRange(Array.isArray(range) ? range : ['', '']);
          setPage(0);
        },
        render: (row) => new Date(row.timestamp).toLocaleString(),
        minWidth: 180,
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        sortable: true,
        sortKey: 'is_vehicle',
        filterValue: isVehicleFilter,
        onFilterChange: (v) => {
          setIsVehicleFilter((v as string) ?? '');
          setPage(0);
        },
        dropdownOptions: vehicleTypeOptions,
        filterAllLabel: 'All',
        render: (row) =>
          row.is_vehicle ? (
            <Chip
              label={row.vehicle_type || 'Vehicle'}
              color="primary"
              size="small"
            />
          ) : (
            <Chip label="Asset" color="default" size="small" />
          ),
        minWidth: 120,
      },
    ],
    [
      assetSearch,
      epcSearch,
      readerFilter,
      timestampDateRange,
      isVehicleFilter,
      readerOptions,
      readers,
    ]
  );


  return (
    <Box sx={{ mt: 3 }}>
      <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="h4" component="h1">
        Asset Scans
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={
          exporting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <FileDownloadIcon />
          )
        }
        onClick={handleExportScans}
        disabled={exporting}
        sx={{mr:2}}
      >
        {exporting ? 'Exporting...' : 'Export'}
      </Button>
    </Box>
      <Card>
        <CardContent>
          <TableView<AssetScanWithNames>
            data={scans}
            headerDetails={headerDetails}
            loading={loading}
            emptyMessage="No asset scans found"
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/assets/${row.asset}`)}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(size) => {
              setRowsPerPage(size);
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssetScanList;
